/**
 * Confirm bridge helpers for the Build Itinerary wizard.
 *
 * After Step 1 extracts a schedule, the wizard must CONFIRM the ingestion into a
 * tournament (creating the CalendarEvent + games) so Step 3 has a
 * calendarEventId to load games from. Team matching is best-effort and
 * user-correctable later: an unambiguous suggested match links to that team,
 * anything else creates a new team (the epic-1 "match if confident, else create,
 * always correctable" rule). These pure helpers keep that mapping testable.
 */

import type {
  TeamMatch,
  TeamResolution,
} from '../../../../../types/schedule-ingestion';

/**
 * Map the review's per-name team matches to confirm resolutions:
 * - a single confident suggestion (not ambiguous) links to that existing team,
 * - otherwise (no match, or ambiguous) leave it UNMATCHED (the game keeps its raw
 *   name with no team linked). We never auto-create a team; creating one is an
 *   explicit user action in the review.
 * Blank raw names are skipped (nothing to resolve).
 */
export function buildAutoTeamResolutions(
  teamMatches: TeamMatch[]
): TeamResolution[] {
  return teamMatches
    .filter((m) => m.rawName.trim().length > 0)
    .map((m) => {
      if (m.suggestedMatch && !m.isAmbiguous) {
        return { rawName: m.rawName, teamId: m.suggestedMatch.teamId };
      }
      return { rawName: m.rawName };
    });
}

/** A non-empty tournament title for confirm (title is required, <= 200 chars). */
export function tournamentTitle(eventName: string): string {
  const trimmed = eventName.trim();
  return (trimmed.length > 0 ? trimmed : 'Imported tournament').slice(0, 200);
}
